import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget — loads the script lazily, renders into a div,
 * and reports the token via onToken callback.
 *
 * Test keys (always pass): site=1x00000000000000000000AA
 */
let scriptPromise = null;
function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function TurnstileWidget({ onToken, theme = "light", size = "flexible" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState("");
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      setError("Turnstile not configured");
      return;
    }

    loadTurnstileScript()
      .then((ts) => {
        if (cancelled || !ts || !containerRef.current) return;
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => onToken && onToken(token),
          "error-callback": () => onToken && onToken(""),
          "expired-callback": () => onToken && onToken(""),
        });
      })
      .catch(() => !cancelled && setError("Failed to load verification"));

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch { /* noop */ }
    };
  }, [siteKey, theme, size, onToken]);

  return (
    <div data-testid="turnstile-widget">
      <div ref={containerRef} />
      {error && <p className="text-xs text-red-600 mt-1" data-testid="turnstile-error">{error}</p>}
    </div>
  );
}
